<script setup lang="ts">
import { computed } from 'vue'

const props = withDefaults(defineProps<{
  name: string
  size?: number | string
  strokeWidth?: number
  fill?: boolean
}>(), {
  size: 18,
  strokeWidth: 1.8,
  fill: false,
})

type IconShape = { paths?: string[]; circles?: Array<[number, number, number]>; rects?: Array<[number, number, number, number, number]> }

const ICONS: Record<string, IconShape> = {
  dashboard: { rects: [[3, 3, 7, 7, 2], [14, 3, 7, 7, 2], [3, 14, 7, 7, 2], [14, 14, 7, 7, 2]] },
  users: { paths: ['M16 21v-2a4 4 0 0 0-4-4H6a4 4 0 0 0-4 4v2', 'M9 11a4 4 0 1 0 0-8 4 4 0 0 0 0 8Z', 'M22 21v-2a4 4 0 0 0-3-3.87', 'M16 3.13a4 4 0 0 1 0 7.75'], circles: [[9, 7, 4]] },
  family: { paths: ['M3 10.5 12 3l9 7.5', 'M5 9.5V21h14V9.5', 'M9 21v-6h6v6'], circles: [[12, 10.2, 2.1]] },
  region: { paths: ['M20 10c0 5-8 11-8 11S4 15 4 10a8 8 0 1 1 16 0Z'], circles: [[12, 10, 2.8]] },
  pill: { paths: ['M10.5 20.5 3.5 13.5a5 5 0 0 1 7-7l7 7a5 5 0 0 1-7 7Z', 'M8 8l8 8'] },
  records: { paths: ['M7 3h10a2 2 0 0 1 2 2v16l-7-3-7 3V5a2 2 0 0 1 2-2Z', 'M9 8h6', 'M9 12h6'] },
  heart: { paths: ['M20.8 4.6a5.5 5.5 0 0 0-7.8 0L12 5.7l-1.1-1.1a5.5 5.5 0 0 0-7.8 7.8l1.1 1.1L12 21l7.8-7.5 1.1-1.1a5.5 5.5 0 0 0-.1-7.8Z'] },
  alert: { paths: ['M10.3 3.9 1.8 18a2 2 0 0 0 1.7 3h17a2 2 0 0 0 1.7-3L13.7 3.9a2 2 0 0 0-3.4 0Z', 'M12 9v4', 'M12 17h.01'] },
  rules: { paths: ['M4 6h10', 'M18 6h2', 'M4 12h3', 'M11 12h9', 'M4 18h8', 'M16 18h4'], circles: [[16, 6, 2], [9, 12, 2], [14, 18, 2]] },
  report: { paths: ['M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8Z', 'M14 2v6h6', 'M8 13h8', 'M8 17h5'] },
  team: { paths: ['M17 21v-2a4 4 0 0 0-4-4H6a4 4 0 0 0-4 4v2', 'M23 21v-2a4 4 0 0 0-3-3.87', 'M16 3.13a4 4 0 0 1 0 7.75'], circles: [[9.5, 7, 4]] },
  device: { rects: [[5, 2, 14, 20, 3]], paths: ['M9 18h6', 'M9 6h6', 'M12 10v4'] },
  drug: { paths: ['M4 8h16v12H4z', 'M8 8V5a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v3', 'M9 12h6', 'M12 9v6'] },
  shield: { paths: ['M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10Z', 'm9 12 2 2 4-4'] },
  bell: { paths: ['M18 8a6 6 0 0 0-12 0c0 7-3 9-3 9h18s-3-2-3-9', 'M13.7 21a2 2 0 0 1-3.4 0'] },
  template: { paths: ['M4 4h16v16H4z', 'M4 9h16', 'M9 21V9'] },
  leaf: { paths: ['M11 20A7 7 0 0 1 4 13c0-6 7-9 16-9 0 9-3 16-9 16Z', 'M4 21c2-7 7-11 13-14'] },
  help: { circles: [[12, 12, 9]], paths: ['M9.1 9a3 3 0 0 1 5.8 1c0 2-3 3-3 3', 'M12 17h.01'] },
  crown: { paths: ['m3 6 4.5 4L12 4l4.5 6L21 6l-2 12H5L3 6Z', 'M5 18h14'] },
  order: { paths: ['M6 2h12l2 5H4l2-5Z', 'M5 7v13a2 2 0 0 0 2 2h10a2 2 0 0 0 2-2V7', 'M9 11h6', 'M9 15h6'] },
  decoration: { paths: ['M4 4h16v16H4z', 'M4 9h16', 'M10 21V9', 'M14 13h3', 'M14 17h3'] },
  link: { paths: ['M10 13a5 5 0 0 0 7.5.5l2-2a5 5 0 0 0-7-7l-1 1', 'M14 11a5 5 0 0 0-7.5-.5l-2 2a5 5 0 0 0 7 7l1-1'] },
  image: { rects: [[3, 3, 18, 18, 2]], circles: [[8.5, 8.5, 1.5]], paths: ['m21 15-5-5L5 21'] },
  ticket: { paths: ['M3 9a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2v2a2 2 0 0 0 0 4v2a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-2a2 2 0 0 0 0-4V9Z', 'M13 7v2', 'M13 15v2', 'M13 11v2'] },
  feedback: { paths: ['M21 15a4 4 0 0 1-4 4H8l-5 3V6a3 3 0 0 1 3-3h11a4 4 0 0 1 4 4v8Z', 'M8 8h8', 'M8 12h5'] },
  analytics: { paths: ['M3 3v18h18', 'm7 16 4-5 4 3 5-7'] },
  settings: { circles: [[12, 12, 3]], paths: ['M19.4 15a1.7 1.7 0 0 0 .34 1.88l.06.06a2 2 0 1 1-2.83 2.83l-.06-.06a1.7 1.7 0 0 0-1.88-.34 1.7 1.7 0 0 0-1 1.55V21a2 2 0 1 1-4 0v-.09a1.7 1.7 0 0 0-1.11-1.55 1.7 1.7 0 0 0-1.88.34l-.06.06a2 2 0 1 1-2.83-2.83l.06-.06A1.7 1.7 0 0 0 4.6 15a1.7 1.7 0 0 0-1.55-1H3a2 2 0 1 1 0-4h.09A1.7 1.7 0 0 0 4.6 8.9a1.7 1.7 0 0 0-.34-1.88l-.06-.06a2 2 0 1 1 2.83-2.83l.06.06A1.7 1.7 0 0 0 8.97 4.6 1.7 1.7 0 0 0 10 3.05V3a2 2 0 1 1 4 0v.09a1.7 1.7 0 0 0 1 1.55 1.7 1.7 0 0 0 1.88-.34l.06-.06a2 2 0 1 1 2.83 2.83l-.06.06a1.7 1.7 0 0 0-.34 1.88V9a1.7 1.7 0 0 0 1.55 1H21a2 2 0 1 1 0 4h-.09a1.7 1.7 0 0 0-1.51 1Z'] },
  audit: { paths: ['M9 3h6l1 2h4v16H4V5h4l1-2Z', 'M8 11h8', 'M8 15h8', 'M8 19h5'] },
  monitor: { rects: [[2, 3, 20, 14, 2]], paths: ['M8 21h8', 'M12 17v4', 'm6 10 3 3 3-5 3 4 3-2'] },
  user: { paths: ['M20 21a8 8 0 0 0-16 0'], circles: [[12, 7, 4]] },
  logout: { paths: ['M9 21H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h4', 'm16 17 5-5-5-5', 'M21 12H9'] },
  search: { circles: [[11, 11, 7]], paths: ['m21 21-4.3-4.3'] },
  calendar: { rects: [[3, 4, 18, 17, 2]], paths: ['M16 2v4', 'M8 2v4', 'M3 10h18'] },
  'chevron-down': { paths: ['m6 9 6 6 6-6'] },
  'chevron-right': { paths: ['m9 18 6-6-6-6'] },
  'chevron-left': { paths: ['m15 18-6-6 6-6'] },
  close: { paths: ['M18 6 6 18', 'm6 6 12 12'] },
  more: { circles: [[5, 12, 1]], paths: ['M12 12h.01', 'M19 12h.01'] },
  plus: { paths: ['M12 5v14', 'M5 12h14'] },
  minus: { paths: ['M5 12h14'] },
  import: { paths: ['M12 3v12', 'm7 10 5 5 5-5', 'M5 21h14'] },
  export: { paths: ['M12 21V9', 'm7 14 5-5 5 5', 'M5 3h14'] },
  refresh: { paths: ['M21 12a9 9 0 1 1-3-6.7L21 8', 'M21 3v5h-5'] },
  check: { paths: ['m4 12 5 5L20 6'] },
  filter: { paths: ['M3 5h18l-7 8v6l-4 2v-8L3 5Z'] },
  columns: { paths: ['M4 4h6v16H4z', 'M14 4h6v16h-6z'] },
  task: { paths: ['M9 11h6', 'M9 15h6', 'M6 3h12a2 2 0 0 1 2 2v16l-4-2-2 2-2-2-2 2-2-2-4 2V5a2 2 0 0 1 2-2Z'] },
  clock: { circles: [[12, 12, 9]], paths: ['M12 7v5l3 2'] },
  warning: { circles: [[12, 12, 9]], paths: ['M12 7v6', 'M12 17h.01'] },
  'check-circle': { circles: [[12, 12, 9]], paths: ['m8 12 3 3 5-6'] },
  info: { circles: [[12, 12, 9]], paths: ['M12 11v5', 'M12 8h.01'] },
  lock: { rects: [[4, 10, 16, 11, 2]], paths: ['M8 10V7a4 4 0 0 1 8 0v3'] },
  key: { circles: [[7.5, 15.5, 4.5]], paths: ['m10.5 12.5 9-9', 'M16 5l3 3', 'M18 3l3 3'] },
  mail: { rects: [[3, 5, 18, 14, 2]], paths: ['m3 6 9 7 9-7'] },
  phone: { paths: ['M22 16.9v3a2 2 0 0 1-2.2 2 19.8 19.8 0 0 1-8.6-3.1 19.5 19.5 0 0 1-6-6A19.8 19.8 0 0 1 2.1 4.2 2 2 0 0 1 4.1 2h3a2 2 0 0 1 2 1.7c.1.9.4 1.8.7 2.6a2 2 0 0 1-.5 2.1L8 9.7a16 16 0 0 0 6.3 6.3l1.3-1.3a2 2 0 0 1 2.1-.5c.8.3 1.7.6 2.6.7a2 2 0 0 1 1.7 2Z'] },
  eye: { paths: ['M2 12s3.5-7 10-7 10 7 10 7-3.5 7-10 7S2 12 2 12Z'], circles: [[12, 12, 3]] },
  edit: { paths: ['M12 20h9', 'M16.5 3.5a2.1 2.1 0 0 1 3 3L7 19l-4 1 1-4L16.5 3.5Z'] },
  trash: { paths: ['M3 6h18', 'M8 6V4h8v2', 'm5 6 1 15h12l1-15', 'M10 11v6', 'M14 11v6'] },
  download: { paths: ['M12 3v12', 'm7 10 5 5 5-5', 'M5 21h14'] },
  upload: { paths: ['M12 21V9', 'm7 14 5-5 5 5', 'M5 3h14'] },
  archive: { rects: [[3, 4, 18, 5, 2], [5, 9, 14, 12, 2]], paths: ['M10 13h4'] },
  file: { paths: ['M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8Z', 'M14 2v6h6', 'M8 13h8', 'M8 17h5'] },
  video: { paths: ['m23 7-7 5 7 5V7Z'], rects: [[3, 5, 14, 14, 2]] },
  scan: { paths: ['M3 7V4h3', 'M17 4h3v3', 'M21 17v3h-3', 'M7 21H4v-3', 'M7 12h10'] },
  layers: { paths: ['m12 2 9 5-9 5-9-5 9-5Z', 'm3 12 9 5 9-5', 'm3 17 9 5 9-5'] },
  gift: { paths: ['M4 10h16v11H4z', 'M2 6h20v4H2z', 'M12 6v15', 'M12 6H7.5a2.5 2.5 0 1 1 0-5C11 1 12 6 12 6Z', 'M12 6h4.5a2.5 2.5 0 1 0 0-5C13 1 12 6 12 6Z'] },
  'arrow-right': { paths: ['M5 12h14', 'm13 6 6 6-6 6'] },
  'arrow-left': { paths: ['M19 12H5', 'm11 18-6-6 6-6'] },
  'wifi-off': { paths: ['m2 2 20 20', 'M8.5 16.5a5 5 0 0 1 7 0', 'M5 12.5a10 10 0 0 1 5-2.7', 'M19 12.5a10 10 0 0 0-2-1.4', 'M2 8.8a16 16 0 0 1 4-2.3', 'M22 8.8a16 16 0 0 0-8-3.3'] },
  wrench: { paths: ['M14.7 6.3a4.5 4.5 0 0 0-6.4 6.4L3 18l3 3 5.3-5.3a4.5 4.5 0 0 0 6.4-6.4l-2.9 2.9-2.1-2.1 2.9-2.9Z'] },
  compass: { circles: [[12, 12, 9]], paths: ['m16.2 7.8-2.1 6.3-6.3 2.1 2.1-6.3 6.3-2.1Z'] },
  server: { rects: [[3, 4, 18, 6, 2], [3, 14, 18, 6, 2]], paths: ['M7 7h.01', 'M7 17h.01', 'M11 7h6', 'M11 17h6'] },
  history: { paths: ['M3 12a9 9 0 1 0 3-6.7L3 8', 'M3 3v5h5', 'M12 7v5l3 2'] },
  menu: { paths: ['M4 6h16', 'M4 12h16', 'M4 18h16'] },
  pin: { paths: ['M12 17v5', 'm5 10 4-8h6l4 8-7 7-7-7Z'] },
  star: { paths: ['m12 2 3.1 6.3 6.9 1-5 4.9 1.2 6.8-6.2-3.3-6.2 3.3L7 14.2 2 9.3l6.9-1L12 2Z'] },
  sparkles: { paths: ['m12 3 1.5 4.5L18 9l-4.5 1.5L12 15l-1.5-4.5L6 9l4.5-1.5L12 3Z', 'M19 15l.8 2.2L22 18l-2.2.8L19 21l-.8-2.2L16 18l2.2-.8L19 15Z', 'M5 2l.6 1.7L7 4l-1.4.5L5 6l-.6-1.5L3 4l1.4-.3L5 2Z'] },
  grid: { paths: ['M4 4h6v6H4z', 'M14 4h6v6h-6z', 'M4 14h6v6H4z', 'M14 14h6v6h-6z'] },
  'panel-left': { rects: [[3, 3, 18, 18, 2]], paths: ['M9 3v18'] },
  'more-horizontal': { paths: ['M5 12h.01', 'M12 12h.01', 'M19 12h.01'] },
  external: { paths: ['M15 3h6v6', 'M10 14 21 3', 'M21 14v5a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h5'] },
}

const shapes = computed(() => ICONS[props.name] ?? ICONS.info)
const size = computed(() => typeof props.size === 'number' ? `${props.size}px` : props.size)
</script>

<template>
  <svg
    class="app-icon"
    :width="size"
    :height="size"
    viewBox="0 0 24 24"
    fill="none"
    :stroke="fill ? 'none' : 'currentColor'"
    :stroke-width="strokeWidth"
    stroke-linecap="round"
    stroke-linejoin="round"
    aria-hidden="true"
    focusable="false"
  >
    <path v-for="(path, index) in shapes.paths" :key="`p-${index}`" :d="path" :fill="fill ? 'currentColor' : 'none'" />
    <circle v-for="(circle, index) in shapes.circles" :key="`c-${index}`" :cx="circle[0]" :cy="circle[1]" :r="circle[2]" :fill="fill ? 'currentColor' : 'none'" />
    <rect
      v-for="(rect, index) in shapes.rects"
      :key="`r-${index}`"
      :x="rect[0]"
      :y="rect[1]"
      :width="rect[2]"
      :height="rect[3]"
      :rx="rect[4]"
      :fill="fill ? 'currentColor' : 'none'"
    />
  </svg>
</template>

<style scoped>
.app-icon { display: block; flex: 0 0 auto; }
</style>
